import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useBudgetAssistantStore } from '@flows.cash/core'
import { useAuthStore } from '@/store/authStore'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { 
  CheckCircle, 
  PieChart, 
  Download, 
  TrendingUp, 
  AlertTriangle,
  Lightbulb,
  Home,
  Coffee,
  PiggyBank,
  Shield,
  Car,
  Loader2,
  Edit3,
  Check,
  X
} from 'lucide-react'

interface ResultsStepProps {
  onComplete: () => void
}

// Currency symbol mapping
const getCurrencySymbol = (currencyCode: string): string => {
  const currencyMap: Record<string, string> = {
    'USD': '$',
    'EUR': '€', 
    'GBP': '£',
    'CAD': 'C$',
    'AUD': 'A$',
    'JPY': '¥',
    'CHF': 'Fr',
    'CNY': '¥',
    'INR': '₹',
    'KRW': '₩',
    'BRL': 'R$',
    'MXN': '$',
    'RUB': '₽',
    'ZAR': 'R',
    'TRY': '₺',
    'SEK': 'kr',
    'NOK': 'kr',
    'DKK': 'kr',
    'PLN': 'zł',
    'CZK': 'Kč',
    'HUF': 'Ft',
  }
  return currencyMap[currencyCode] || currencyCode
}

export function ResultsStep({ onComplete }: ResultsStepProps) {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const { results, generateResults, generateBudgets, completeSession, responses, isLoading: storeLoading } = useBudgetAssistantStore()
  const [isGeneratingPlan, setIsGeneratingPlan] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [localResults, setLocalResults] = useState(results)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [editAmounts, setEditAmounts] = useState<Record<number, string>>({})
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  // Generate results when component mounts
  useEffect(() => {
    try {
      const generatedResults = generateResults()
      setLocalResults(generatedResults)
    } catch (error) {
      console.error('Error generating results:', error)
    }
  }, [generateResults, responses])

  const handleCreateBudget = async () => {
    if (!user?.id || !localResults) return

    setIsGeneratingPlan(true)
    try {
      await generateBudgets(user.id)
      await completeSession(user.id)
      onComplete()
    } catch (error) {
      console.error('Error creating budgets:', error)
    } finally {
      setIsGeneratingPlan(false)
    }
  }

  const handleDownloadPlan = async () => {
    if (!localResults) return

    setIsDownloading(true)
    try {
      // Dynamically import jsPDF to avoid SSR issues
      const { jsPDF } = await import('jspdf')
      
      const doc = new jsPDF()
      const { totalIncome, totalExpenses, totalSavings, remainingIncome, suggestions, tips, warnings, currency } = localResults
      const currencySymbol = getCurrencySymbol(currency || 'USD')
      
      // Set up the document
      doc.setFontSize(20)
      doc.setFont('helvetica', 'bold')
      doc.text('Personal Budget Plan', 20, 30)
      
      doc.setFontSize(12)
      doc.setFont('helvetica', 'normal')
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, 40)
      doc.text(`Created by: ${user?.email || 'Budget Assistant'}`, 20, 50)
      
      // Budget Overview Section
      doc.setFontSize(16)
      doc.setFont('helvetica', 'bold')
      doc.text('Budget Overview', 20, 70)
      
      doc.setFontSize(12)
      doc.setFont('helvetica', 'normal')
      doc.text(`Monthly Income: ${currencySymbol}${totalIncome.toLocaleString()}`, 20, 85)
      doc.text(`Total Expenses: ${currencySymbol}${totalExpenses.toLocaleString()}`, 20, 95)
      doc.text(`Planned Savings: ${currencySymbol}${totalSavings.toLocaleString()}`, 20, 105)
      
      const remainingText = remainingIncome >= 0 ? 'Remaining' : 'Over Budget'
      doc.text(`${remainingText}: ${currencySymbol}${Math.abs(remainingIncome).toLocaleString()}`, 20, 115)
      
      // Budget Breakdown Section
      doc.setFontSize(16)
      doc.setFont('helvetica', 'bold')
      doc.text('Budget Breakdown', 20, 140)
      
      let yPosition = 155
      doc.setFontSize(11)
      doc.setFont('helvetica', 'normal')
      
      suggestions.forEach((suggestion, index) => {
        const percentage = ((suggestion.amount / totalIncome) * 100).toFixed(1)
        doc.text(`${suggestion.categoryName}:`, 20, yPosition)
        doc.text(`${currencySymbol}${suggestion.amount.toLocaleString()} (${percentage}%)`, 120, yPosition)
        yPosition += 10
        
        // Add new page if needed
        if (yPosition > 270) {
          doc.addPage()
          yPosition = 30
        }
      })
      
      // Tips Section
      if (tips.length > 0) {
        yPosition += 10
        if (yPosition > 250) {
          doc.addPage()
          yPosition = 30
        }
        
        doc.setFontSize(16)
        doc.setFont('helvetica', 'bold')
        doc.text('Personalized Tips', 20, yPosition)
        yPosition += 15
        
        doc.setFontSize(11)
        doc.setFont('helvetica', 'normal')
        
        tips.forEach((tip, index) => {
          // Split long tips into multiple lines
          const splitTip = doc.splitTextToSize(tip, 170)
          doc.text(splitTip, 20, yPosition)
          yPosition += splitTip.length * 5 + 5
          
          if (yPosition > 270) {
            doc.addPage()
            yPosition = 30
          }
        })
      }
      
      // Warnings Section
      if (warnings.length > 0) {
        yPosition += 10
        if (yPosition > 250) {
          doc.addPage()
          yPosition = 30
        }
        
        doc.setFontSize(16)
        doc.setFont('helvetica', 'bold')
        doc.text('Important Considerations', 20, yPosition)
        yPosition += 15
        
        doc.setFontSize(11)
        doc.setFont('helvetica', 'normal')
        
        warnings.forEach((warning, index) => {
          const splitWarning = doc.splitTextToSize(warning, 170)
          doc.text(splitWarning, 20, yPosition)
          yPosition += splitWarning.length * 5 + 5
          
          if (yPosition > 270) {
            doc.addPage()
            yPosition = 30
          }
        })
      }
      
      // Footer
      const pageCount = doc.getNumberOfPages()
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i)
        doc.setFontSize(10)
        doc.setFont('helvetica', 'normal')
        doc.text(`Generated by Flows.Cash Budget Assistant - Page ${i} of ${pageCount}`, 20, 285)
      }
      
      // Save the PDF
      const fileName = `budget-plan-${new Date().toISOString().split('T')[0]}.pdf`
      doc.save(fileName)
      
    } catch (error) {
      console.error('Error downloading budget plan:', error)
      // You could add a toast notification here for error feedback
    } finally {
      setIsDownloading(false)
    }
  }

  const handleEditStart = (index: number, currentAmount: number) => {
    setEditingIndex(index)
    setEditAmounts(prev => ({ ...prev, [index]: currentAmount.toString() }))
  }

  const handleEditSave = (index: number) => {
    const newAmount = parseFloat(editAmounts[index] || '0')
    if (!isNaN(newAmount) && newAmount >= 0 && localResults) {
      const updatedSuggestions = [...localResults.suggestions]
      const oldAmount = updatedSuggestions[index].amount
      updatedSuggestions[index] = { ...updatedSuggestions[index], amount: newAmount }
      
      // Recalculate totals
      const totalExpensesChange = newAmount - oldAmount
      const newTotalExpenses = localResults.totalExpenses + totalExpensesChange
      const newRemainingIncome = localResults.totalIncome - newTotalExpenses - localResults.totalSavings
      
      setLocalResults(prev => prev ? {
        ...prev,
        suggestions: updatedSuggestions,
        totalExpenses: newTotalExpenses,
        remainingIncome: newRemainingIncome
      } : null)
      
      setHasUnsavedChanges(true)
    }
    setEditingIndex(null)
    setEditAmounts(prev => {
      const newAmounts = { ...prev }
      delete newAmounts[index]
      return newAmounts
    })
  }

  const handleEditCancel = (index: number) => {
    setEditingIndex(null)
    setEditAmounts(prev => {
      const newAmounts = { ...prev }
      delete newAmounts[index]
      return newAmounts
    })
  }

  if (!localResults) {
    return (
      <div className="max-w-4xl mx-auto space-y-8">
        <div className="text-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin mx-auto text-blue-600" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t('budgetAssistant.results.generating', 'Generating your budget plan...')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            {t('budgetAssistant.results.generatingDesc', 'Please wait while we analyze your responses and create a personalized budget.')}
          </p>
        </div>
      </div>
    )
  }

  const { totalIncome, totalExpenses, totalSavings, remainingIncome, suggestions, tips, warnings, currency } = localResults
  const currencySymbol = getCurrencySymbol(currency || 'USD')

  const getBudgetHealthColor = () => {
    const ratio = totalExpenses / totalIncome
    if (ratio <= 0.5) return 'text-green-600'
    if (ratio <= 0.7) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getBudgetHealthStatus = () => {
    const ratio = totalExpenses / totalIncome
    if (ratio <= 0.5) return t('budgetAssistant.results.health.excellent', 'Excellent')
    if (ratio <= 0.7) return t('budgetAssistant.results.health.good', 'Good')
    return t('budgetAssistant.results.health.needsWork', 'Needs Work')
  }

  // Get category icon based on name
  const getCategoryIcon = (categoryName: string) => {
    const name = categoryName.toLowerCase()
    if (name.includes('housing') || name.includes('rent')) return Home
    if (name.includes('food') || name.includes('dining') || name.includes('groceries')) return Coffee
    if (name.includes('transportation') || name.includes('car')) return Car
    if (name.includes('savings')) return PiggyBank
    if (name.includes('emergency')) return Shield
    return TrendingUp
  }

  // Get category color based on priority
  const getCategoryColor = (priority: string) => {
    switch (priority) {
      case 'essential': return '#E53935'
      case 'important': return '#FB8C00' 
      case 'flexible': return '#8E24AA'
      default: return '#43A047'
    }
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center space-y-3">
        <div className="relative mx-auto w-16 h-16 mb-4">
          <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl opacity-20 animate-pulse"></div>
          <div className="relative bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-4 shadow-lg">
            <CheckCircle className="h-8 w-8 text-white" />
          </div>
        </div>
        
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {t('budgetAssistant.results.title', 'Your Personalized Budget Plan')}
        </h1>
        <p className="text-base text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
          {t('budgetAssistant.results.subtitle', 'Based on your responses, here\'s a budget plan tailored just for you!')}
        </p>
        {hasUnsavedChanges && (
          <Badge variant="outline" className="bg-orange-50 border-orange-200 text-orange-700">
            {t('budgetAssistant.results.unsavedChanges', 'You have unsaved changes')}
          </Badge>
        )}
      </div>

      {/* Budget Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-5 text-center bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 border-green-200 dark:border-green-800">
          <div className="text-2xl font-bold text-green-800 dark:text-green-200">
            {currencySymbol}{totalIncome.toLocaleString()}
          </div>
          <div className="text-sm text-green-600 dark:text-green-400">
            {t('budgetAssistant.results.overview.income', 'Monthly Income')}
          </div>
        </Card>
        
        <Card className="p-5 text-center bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-950/20 dark:to-orange-950/20 border-red-200 dark:border-red-800">
          <div className="text-2xl font-bold text-red-800 dark:text-red-200">
            {currencySymbol}{totalExpenses.toLocaleString()}
          </div>
          <div className="text-sm text-red-600 dark:text-red-400">
            {t('budgetAssistant.results.overview.expenses', 'Total Expenses')}
          </div>
        </Card>
        
        <Card className="p-5 text-center bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border-blue-200 dark:border-blue-800">
          <div className="text-2xl font-bold text-blue-800 dark:text-blue-200">
            {currencySymbol}{totalSavings.toLocaleString()}
          </div>
          <div className="text-sm text-blue-600 dark:text-blue-400">
            {t('budgetAssistant.results.overview.savings', 'Planned Savings')}
          </div>
        </Card>
        
        <Card className="p-5 text-center bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/20 dark:to-pink-950/20 border-purple-200 dark:border-purple-800">
          <div className="text-2xl font-bold text-purple-800 dark:text-purple-200">
            {currencySymbol}{Math.abs(remainingIncome).toLocaleString()}
          </div>
          <div className="text-sm text-purple-600 dark:text-purple-400">
            {remainingIncome >= 0 ? 
              t('budgetAssistant.results.overview.remaining', 'Remaining') : 
              t('budgetAssistant.results.overview.deficit', 'Over Budget')
            }
          </div>
        </Card>
      </div>

      {/* Budget Breakdown Chart Placeholder */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <PieChart className="h-5 w-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t('budgetAssistant.results.breakdown.title', 'Budget Breakdown')}
            </h3>
          </div>
          <div className="text-xs text-gray-500">
            {t('budgetAssistant.results.breakdown.editHint', 'Click edit to adjust amounts')}
          </div>
        </div>
        
        <div className="space-y-3">
          {suggestions.map((suggestion, index) => {
            const percentage = ((suggestion.amount / totalIncome) * 100).toFixed(1)
            const isEditing = editingIndex === index
            
            return (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 rounded-full bg-blue-500"></div>
                  <span className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                    {suggestion.categoryName}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    {isEditing ? (
                      <div className="flex items-center gap-2">
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                            {currencySymbol}
                          </span>
                          <Input
                            type="number"
                            value={editAmounts[index] || ''}
                            onChange={(e) => setEditAmounts(prev => ({ ...prev, [index]: e.target.value }))}
                            className="w-24 h-8 pl-6 text-sm"
                            min="0"
                            step="0.01"
                            autoFocus
                          />
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditSave(index)}
                          className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditCancel(index)}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
                          {currencySymbol}{suggestion.amount.toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-500">
                          {percentage}%
                        </div>
                      </>
                    )}
                  </div>
                  {!isEditing && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEditStart(index, suggestion.amount)}
                      className="h-8 w-8 p-0 text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                    >
                      <Edit3 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
        
        {hasUnsavedChanges && (
          <div className="mt-4 p-3 bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 rounded-lg">
            <div className="flex items-center gap-2 text-orange-800 dark:text-orange-200 text-sm">
              <AlertTriangle className="h-4 w-4" />
              {t('budgetAssistant.results.breakdown.changesNote', 'Your changes will be applied when you create the budget.')}
            </div>
          </div>
        )}
      </Card>

      {/* Personalized Tips */}
      {tips.length > 0 && (
        <Card className="p-5">
          <div className="flex items-center gap-3 mb-4">
            <Lightbulb className="h-5 w-5 text-yellow-600" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t('budgetAssistant.results.tips.title', 'Personalized Tips')}
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {tips.map((tip, index) => (
              <div key={index} className="p-4 bg-yellow-50/50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  {tip}
                </p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Warnings */}
      {warnings.length > 0 && (
        <Card className="p-5 bg-orange-50/50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-800">
          <div className="flex items-center gap-3 mb-3">
            <AlertTriangle className="h-5 w-5 text-orange-600" />
            <h3 className="text-lg font-semibold text-orange-900 dark:text-orange-100">
              {t('budgetAssistant.results.warnings.title', 'Things to Consider')}
            </h3>
          </div>
          <div className="space-y-2">
            {warnings.map((warning, index) => (
              <p key={index} className="text-sm text-orange-800 dark:text-orange-200 flex items-start gap-2">
                <span className="text-orange-500 mt-0.5">•</span>
                {warning}
              </p>
            ))}
          </div>
        </Card>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
        <Button
          variant="outline"
          onClick={handleDownloadPlan}
          disabled={isDownloading}
          className="flex items-center gap-2 h-11"
        >
          {isDownloading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
              {t('budgetAssistant.results.actions.downloading', 'Downloading...')}
            </>
          ) : (
            <>
              <Download className="h-4 w-4" />
              {t('budgetAssistant.results.actions.download', 'Download Plan')}
            </>
          )}
        </Button>
        
        <Button
          onClick={handleCreateBudget}
          disabled={isGeneratingPlan}
          className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-8 flex items-center gap-2 h-11"
        >
          {isGeneratingPlan ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              {t('budgetAssistant.results.actions.creating', 'Creating Budget...')}
            </>
          ) : (
            <>
              <CheckCircle className="h-4 w-4" />
              {t('budgetAssistant.results.actions.create', 'Create This Budget')}
            </>
          )}
        </Button>
      </div>

      {/* Next Steps */}
      <Card className="p-5 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border-blue-200 dark:border-blue-800">
        <div className="text-center space-y-3">
          <h4 className="font-semibold text-blue-900 dark:text-blue-100 text-base">
            {t('budgetAssistant.results.nextSteps.title', 'What happens next?')}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="text-center">
              <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-semibold mx-auto mb-2">
                1
              </div>
              <p className="text-blue-800 dark:text-blue-200">
                {t('budgetAssistant.results.nextSteps.step1', 'Budgets will be created in your account')}
              </p>
            </div>
            <div className="text-center">
              <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-semibold mx-auto mb-2">
                2
              </div>
              <p className="text-blue-800 dark:text-blue-200">
                {t('budgetAssistant.results.nextSteps.step2', 'Start tracking your expenses')}
              </p>
            </div>
            <div className="text-center">
              <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-semibold mx-auto mb-2">
                3
              </div>
              <p className="text-blue-800 dark:text-blue-200">
                {t('budgetAssistant.results.nextSteps.step3', 'Adjust as needed over time')}
              </p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
} 